import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  TouchApp as TouchIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  FiberManualRecord as DotIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import { dashboardService } from '../services/dashboardService';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(sizeStr: string): string {
  return sizeStr; // already formatted by pg_size_pretty
}

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  live?: boolean;
}

function KpiCard({ title, value, subtitle, icon, color, live }: KpiCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
              {live && <DotIcon sx={{ fontSize: 10, color: 'success.main', ml: 0.5, animation: 'pulse 2s infinite' }} />}
            </Typography>
            <Typography variant="h4" fontWeight="bold">{value}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}.50`, color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SystemMonitor() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['system-monitor'],
    queryFn: () => dashboardService.getSystemMonitor(),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error" sx={{ m: 2 }}>Error cargando datos del monitor: {(error as Error)?.message}</Alert>;
  }

  // Activity chart data (24h)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hourMap = new Map(data.interactionsByHour.map((h: any) => [h.hour, h.count]));
  const activityChartData = {
    labels: hours.map(h => `${h.toString().padStart(2, '0')}:00`),
    datasets: [{
      label: 'Interacciones',
      data: hours.map(h => hourMap.get(h) || 0),
      backgroundColor: 'rgba(25, 118, 210, 0.6)',
      borderRadius: 4,
    }],
  };

  // Role distribution chart
  const roleColors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f', '#0097a7', '#5d4037'];
  const roleChartData = {
    labels: data.usersByRole.map((r: any) => r.role),
    datasets: [{
      data: data.usersByRole.map((r: any) => r.count),
      backgroundColor: roleColors.slice(0, data.usersByRole.length),
    }],
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Monitor del Sistema
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Actualizado cada 30 segundos
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Usuarios Activos" value={data.activeUsersNow} subtitle={`${data.activeUsersToday} hoy`} icon={<PeopleIcon />} color="success" live />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Usuarios Total" value={data.totalUsers} subtitle={`+${data.newUsersToday} hoy`} icon={<PeopleIcon />} color="primary" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Interacciones Hoy" value={data.totalInteractionsToday} icon={<TouchIcon />} color="info" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Emergencias Activas" value={data.activeEmergencies} subtitle={`${data.totalEmergencies} total`} icon={<WarningIcon />} color={data.activeEmergencies > 0 ? 'error' : 'warning'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Errores Hoy" value={data.errorsToday} subtitle={`${data.errorsThisWeek} esta semana`} icon={<BugIcon />} color={data.errorsToday > 0 ? 'error' : 'success'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Uptime Backend" value={formatUptime(data.backendUptime)} icon={<TimerIcon />} color="success" />
        </Grid>
      </Grid>

      {/* Activity Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Actividad PWA (24h)</Typography>
          <Box sx={{ height: 250 }}>
            <Bar data={activityChartData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }} />
          </Box>
        </CardContent>
      </Card>

      {/* Two-column: Top Screens + Role Distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Pantallas Más Visitadas</Typography>
              {data.interactionsByScreen.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin datos de interacciones aún</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pantalla</TableCell>
                        <TableCell align="right">Visitas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.interactionsByScreen.map((row: any) => (
                        <TableRow key={row.screen_name}>
                          <TableCell>{row.screen_name}</TableCell>
                          <TableCell align="right"><strong>{row.count}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Usuarios por Rol</Typography>
              <Box sx={{ maxWidth: 280, mx: 'auto' }}>
                <Doughnut data={roleChartData} options={{ plugins: { legend: { position: 'bottom' } } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Health + Enrollment */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Infraestructura</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Base de datos</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatBytes(data.dbSize)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Conexiones DB</Typography>
                  <Typography variant="body2">{data.dbConnections.active} / {data.dbConnections.total}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Redis</Typography>
                  <Chip size="small" label={data.redisConnected ? 'Conectado' : 'Desconectado'} color={data.redisConnected ? 'success' : 'error'} />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Tiempo respuesta emergencias</Typography>
                  <Typography variant="body2">{data.avgResponseTimeSec}s promedio</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Enrollment</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Participantes total</Typography>
                  <Typography variant="body2" fontWeight="bold">{data.totalParticipants}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Conversaciones activas</Typography>
                  <Typography variant="body2">{data.activeConversations}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Nuevos usuarios (semana)</Typography>
                  <Typography variant="body2">{data.newUsersThisWeek}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Errores Recientes</Typography>
              {data.recentErrors.length === 0 ? (
                <Typography variant="body2" color="success.main">Sin errores recientes</Typography>
              ) : (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {data.recentErrors.map((err: any, i: number) => (
                    <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="error">{err.action}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(err.time).toLocaleString('es-CL')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
